import type { ProblemDetails } from '@agents/shared';
import type { AuthClient } from './auth';

export class ApiProblem extends Error {
  constructor(readonly problem: ProblemDetails) {
    super(problem.detail ?? problem.title);
  }

  get status() {
    return this.problem.status;
  }

  fieldErrors(): Record<string, string> {
    return Object.fromEntries((this.problem.errors ?? []).map((e) => [e.path, e.message]));
  }
}

const TENANT_KEY = 'agents.tenantId';

export const tenantSelection = {
  get(): string | null {
    try {
      return localStorage.getItem(TENANT_KEY);
    } catch {
      return null;
    }
  },
  set(id: string | null) {
    try {
      if (id) localStorage.setItem(TENANT_KEY, id);
      else localStorage.removeItem(TENANT_KEY);
    } catch {
      /* storage unavailable: selection falls back to the first membership */
    }
  },
};

export interface ApiClient {
  request<T>(path: string, init?: { method?: string; body?: unknown }): Promise<T>;
  /** Opens an SSE stream. The ID token travels in x-agents-authorization (see ADR-012). */
  stream(path: string, init: { signal: AbortSignal; lastEventId?: number }): Promise<Response>;
  /**
   * Sends a file with an upload ticket. S3 presigned POSTs (absolute URLs) get no credentials;
   * the local development endpoint (/api/...) is called like any other API request.
   */
  upload(url: string, fields: Record<string, string>, file: File): Promise<void>;
}

export function createApiClient(baseUrl: string, auth: AuthClient, onUnauthorized: () => void): ApiClient {
  return {
    async upload(url, fields, file) {
      const form = new FormData();
      for (const [k, v] of Object.entries(fields)) form.append(k, v);
      form.append('file', file);
      const toApi = url.startsWith('/');
      let headers: Record<string, string> = {};
      if (toApi) {
        const session = await auth.getSession();
        if (!session) {
          onUnauthorized();
          throw new ApiProblem({ type: 'about:blank', title: 'No autenticado', status: 401 });
        }
        const tenant = tenantSelection.get();
        headers = { ...session.headers, ...(tenant ? { 'x-tenant-id': tenant } : {}) };
      }
      let res: Response;
      try {
        res = await fetch(url, { method: 'POST', headers, body: form });
      } catch {
        throw new ApiProblem({
          type: 'about:blank',
          title: 'Sin conexión',
          status: 0,
          detail: 'No pudimos cargar el archivo. Revisa tu conexión e intenta de nuevo.',
        });
      }
      if (!res.ok) {
        throw new ApiProblem({
          type: 'about:blank',
          title: 'No se pudo cargar el archivo',
          status: res.status,
          detail:
            res.status === 413 || res.status === 400
              ? 'El archivo no coincide con el tamaño permitido.'
              : 'Intenta de nuevo en unos minutos.',
        });
      }
    },
    async stream(path, { signal, lastEventId }) {
      const session = await auth.getSession();
      if (!session) {
        onUnauthorized();
        throw new ApiProblem({ type: 'about:blank', title: 'No autenticado', status: 401 });
      }
      const { authorization, ...rest } = session.headers;
      const tenant = tenantSelection.get();
      return fetch(`${baseUrl}${path}`, {
        signal,
        headers: {
          accept: 'text/event-stream',
          ...rest,
          ...(authorization ? { 'x-agents-authorization': authorization } : {}),
          ...(tenant ? { 'x-tenant-id': tenant } : {}),
          ...(lastEventId ? { 'last-event-id': String(lastEventId) } : {}),
        },
      });
    },
    async request<T>(path: string, init: { method?: string; body?: unknown } = {}): Promise<T> {
      const session = await auth.getSession();
      if (!session) {
        onUnauthorized();
        throw new ApiProblem({ type: 'about:blank', title: 'No autenticado', status: 401 });
      }
      const tenant = tenantSelection.get();
      let res: Response;
      try {
        res = await fetch(`${baseUrl}${path}`, {
          method: init.method ?? 'GET',
          headers: {
            accept: 'application/json',
            ...session.headers,
            ...(tenant ? { 'x-tenant-id': tenant } : {}),
            ...(init.body !== undefined ? { 'content-type': 'application/json' } : {}),
          },
          body: init.body === undefined ? undefined : JSON.stringify(init.body),
        });
      } catch {
        throw new ApiProblem({
          type: 'about:blank',
          title: 'Sin conexión',
          status: 0,
          detail: 'No pudimos comunicarnos con el servidor. Revisa tu conexión e intenta de nuevo.',
        });
      }
      if (res.status === 401) onUnauthorized();
      if (res.status === 204) return undefined as T;
      const text = await res.text();
      const data = text ? JSON.parse(text) : null;
      if (!res.ok) {
        throw new ApiProblem(
          data && typeof data === 'object' && 'title' in data
            ? (data as ProblemDetails)
            : { type: 'about:blank', title: 'Error', status: res.status },
        );
      }
      return data as T;
    },
  };
}
